import * as html from 'smg/html';

// The mobile sheet keeps at least a 72px top gap (the phone header's height);
// while the taller tablet header (contact bar + logo row) is in view the sheet
// starts below it instead. The header is not sticky, so once it scrolls away
// the minimum gap is what remains.
function sheetHeight(): number {
    const headerBottom = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0;
    return window.innerHeight - Math.max(72, headerBottom + 8);
}

const dropdowns = document.querySelectorAll<HTMLDivElement>('.dropdown');
dropdowns.forEach(dropdown => {
    // Add classes and parameters to dropdowns to match default styles, but allow for customization
    if (!dropdown.classList.contains('dropdown-start') && !dropdown.classList.contains('dropdown-end')) {
        dropdown.classList.add('dropdown-responsive', 'dropdown-center');
    }

    // Add offset so the menu is positioned correctly under the button
    const btn = dropdown.querySelector<HTMLButtonElement>('button[data-bs-toggle="dropdown"]');
    const link = dropdown.querySelector<HTMLAnchorElement>('.dropdown-toggle');
    const menu = dropdown.querySelector<HTMLUListElement>('.dropdown-menu');
    if (!menu) {
        return;
    }
    menu.style.setProperty('--smg-dropdown-mobile-height', '0');

    const toggler = btn || link;
    if (!toggler) {
        return;
    }

    if (!toggler.getAttribute('data-bs-offset')) {
        toggler.setAttribute('data-bs-offset', `0,-${toggler.offsetHeight / 2}`);
    }

    const preventDropdownOverflow = () => {
        if (menu.getBoundingClientRect().bottom > window.innerHeight) {
            menu.style.maxHeight = `${menu.offsetHeight - (menu.getBoundingClientRect().bottom - window.innerHeight) - 16}px`;
            menu.style.overflowY = 'scroll';
        }
    };

    const close = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        menu.style.setProperty('--smg-dropdown-mobile-height', '0px'); // start closing animation
        const backdrop = document.querySelector<HTMLDivElement>('.dropdown-full-backdrop');
        if (backdrop) {
            backdrop.style.opacity = '0';
        }

        setTimeout(() => {
            document.dispatchEvent(new MouseEvent('click'));
            document.querySelector<HTMLDivElement>('.dropdown-full-backdrop')?.remove();
        }, 200); // wait for closing animation to finish
    };

    toggler.addEventListener('click', () => {
        document.body.append(html.FromString('<div class="dropdown-full-backdrop"></div>'));
        const backdrop = document.querySelector<HTMLDivElement>('.dropdown-full-backdrop');
        if (backdrop) {
            backdrop.style.transition = 'opacity 0.2s';
            setTimeout(() => backdrop.style.opacity = '0.5'); // start opening animation
            backdrop.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();
                close(e);
            });
        }

        /* We are calculating this height in JS instead of CSS because many mobile browsers include the
        space behind the address bar when they calculate vh, so the menu looks taller than it should be.
        window.innerHeight specifically doesn't include browser UI, so the appearance ends up correct.
        The height in CSS was calc(100vh - 4.5rem) */
        setTimeout(() => {
            menu.style.setProperty('--smg-dropdown-mobile-height', `${sheetHeight()}px`);
            preventDropdownOverflow();
        });

        window.addEventListener('resize', () => {
            menu.style.maxHeight = '';
            preventDropdownOverflow();
        });
    });

    menu?.insertBefore(html.FromString('<div class="dragbar"></div>'), menu.firstChild);

    const dragbar = dropdown.querySelector<HTMLDivElement>('.dragbar');
    if (!dragbar) {
        return;
    }

    let initTransition = '';
    let halfway = window.innerHeight / 2;
    const update = (e: TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.targetTouches[0].target === dragbar) {
            menu.style.setProperty('--smg-dropdown-mobile-height', `${window.innerHeight - e.targetTouches[0].clientY}px`);
        }
    };
    let touchStartTime = 0;
    dragbar.addEventListener('touchstart', e => {
        e.preventDefault();
        e.stopPropagation();
        touchStartTime = Date.now();
        initTransition = menu.style.transition;
        halfway = menu.offsetHeight / 2 + (window.innerHeight - menu.offsetHeight);
        menu.style.transition = 'none';
        dragbar.addEventListener('touchmove', update);
    });
    dragbar.addEventListener('touchend', e => {
        e.preventDefault();
        e.stopPropagation();
        dragbar.removeEventListener('touchmove', update);
        menu.style.transition = initTransition;
        if (Date.now() - touchStartTime < 200) {
            close(e);
            return;
        }
        if (menu.offsetHeight > halfway) {
            menu.style.setProperty('--smg-dropdown-mobile-height', `${sheetHeight()}px`);
        } else {
            close(e);
        }
    });
    dragbar.addEventListener('click', e => {
        dragbar.removeEventListener('touchmove', update);
        e.preventDefault();
        e.stopPropagation();
        close(e);
    });

    const updateDragbarShadow = () => {
        if (menu.scrollTop < 5) {
            dragbar.classList.remove('shadow');
        } else if (menu.scrollTop >= 10) {
            dragbar.classList.add('shadow');
        }
    };

    let menuScrollThrottle = false;
    menu.addEventListener('scroll', () => {
        if (!menuScrollThrottle) {
            menuScrollThrottle = true;
            setTimeout(() => menuScrollThrottle = false, 50);
            updateDragbarShadow();
        }
    });
    menu.addEventListener('scrollend', updateDragbarShadow);

    // Prevent the menu from closing when clicking between links
    menu.addEventListener('click', e => {
        if (!(e.target instanceof HTMLAnchorElement)) {
            e.preventDefault();
            e.stopPropagation();
        }
    });
});
